import React, { useCallback, useEffect, useRef, useState } from "react";
import { Send, Loader2, RefreshCcw, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatDistanceToNow } from "date-fns";

/**
 * Reusable polling chat panel.
 *
 * Props:
 *   messagesEndpoint(after) → returns axios promise resolving to {data:{messages, server_time}}
 *   sendEndpoint(body)      → returns axios promise resolving to the created message
 *   viewerType              → "bdc" | "customer" (drives alignment + colors)
 *   isClosed                → disables input if true
 *   pollingMs               → default 3000
 *   emptyStateText          → shown when no messages yet
 *   testIdPrefix            → to build data-testids
 */
export const ChatPanel = ({
  messagesEndpoint,
  sendEndpoint,
  viewerType = "customer",
  isClosed = false,
  pollingMs = 3000,
  emptyStateText = "No messages yet — say hello!",
  testIdPrefix = "chat",
}) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const cursorRef = useRef(null);
  const listRef = useRef(null);
  const pollRef = useRef(null);

  const scrollToEnd = () => {
    setTimeout(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, 20);
  };

  const fetchMessages = useCallback(async (initial = false) => {
    try {
      const res = await messagesEndpoint(initial ? null : cursorRef.current);
      const data = res?.data || res || {};
      const incoming = data.messages || [];
      if (initial) {
        // Initial fetch: replace, don't merge — this guarantees a fresh reopen
        // shows the full history (never a stale empty state).
        setMessages(incoming);
        if (incoming.length) cursorRef.current = incoming[incoming.length - 1].created_at;
      } else if (incoming.length) {
        setMessages((prev) => {
          const known = new Set(prev.map((m) => m.id));
          return [...prev, ...incoming.filter((m) => !known.has(m.id))];
        });
        cursorRef.current = incoming[incoming.length - 1].created_at;
      }
      if (initial) setLoadError(null);
    } catch (e) {
      // Surface initial-load errors so the user can retry instead of staring
      // at an empty drawer. Polling errors are still swallowed (transient).
      if (initial) {
        const status = e?.response?.status;
        setLoadError(
          status === 401 || status === 403
            ? "This chat link has expired. Submit a new request to start a fresh conversation."
            : "Couldn't load messages. Check your connection and try again."
        );
      }
    } finally {
      if (initial) setLoading(false);
    }
  }, [messagesEndpoint]);

  // On mount (or when the endpoint identity changes because the ChatDrawer key
  // switched threads), run an initial fetch.
  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    cursorRef.current = null;
    fetchMessages(true).then(() => scrollToEnd());
    return undefined;
  }, [fetchMessages]);

  useEffect(() => {
    pollRef.current = setInterval(() => fetchMessages(false), pollingMs);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages, pollingMs]);

  useEffect(() => { scrollToEnd(); }, [messages.length]);

  const retry = () => {
    setLoading(true);
    setLoadError(null);
    fetchMessages(true).then(() => scrollToEnd());
  };

  const doSend = async (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body || sending || isClosed) return;
    setSending(true);
    try {
      const res = await sendEndpoint(body);
      const created = res?.data || res;
      setMessages((prev) => (prev.find((m) => m.id === created.id) ? prev : [...prev, created]));
      cursorRef.current = created.created_at;
      setText("");
    } catch (e) {
      // no-op
    } finally {
      setSending(false);
    }
  };

  const bubbleStyle = (m) => {
    if (m.sender_type === "system") {
      return "self-center bg-slate-100 text-slate-500 text-xs px-3 py-1 rounded-full max-w-[80%] text-center";
    }
    const mine =
      (viewerType === "bdc" && m.sender_type === "bdc") ||
      (viewerType === "customer" && m.sender_type === "customer");
    if (mine) {
      return "self-end bg-brand text-white rounded-2xl rounded-br-md px-3.5 py-2 max-w-[80%]";
    }
    if (m.sender_type === "bdc") {
      return "self-start bg-white border border-slate-200 text-slate-800 rounded-2xl rounded-bl-md px-3.5 py-2 max-w-[80%] shadow-sm";
    }
    return "self-start bg-slate-100 text-slate-800 rounded-2xl rounded-bl-md px-3.5 py-2 max-w-[80%]";
  };

  return (
    <div className="flex flex-col h-full min-h-[420px] bg-slate-50 rounded-xl border border-slate-200 overflow-hidden" data-testid={`${testIdPrefix}-panel`}>
      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2">
        {loading ? (
          <div className="flex items-center justify-center h-full text-slate-400 text-sm gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500 text-sm gap-3 px-6 text-center" data-testid={`${testIdPrefix}-error`}>
            <AlertCircle size={20} className="text-rose-500" />
            <p>{loadError}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={retry}
              data-testid={`${testIdPrefix}-retry-btn`}
            >
              <RefreshCcw size={13} /> Try again
            </Button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-400 text-sm">
            {emptyStateText}
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className="flex flex-col" data-testid={`${testIdPrefix}-msg-${m.id}`}>
              <div className={bubbleStyle(m)}>
                {m.sender_type !== "system" && (
                  <p className="text-[10px] font-semibold opacity-70 mb-0.5">
                    {m.sender_name}
                  </p>
                )}
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
                <p className="text-[10px] opacity-60 mt-0.5">
                  {m.created_at ? formatDistanceToNow(new Date(m.created_at), { addSuffix: true }) : ""}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
      <form onSubmit={doSend} className="border-t border-slate-200 bg-white p-2 flex gap-2 items-end" data-testid={`${testIdPrefix}-form`}>
        <Textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); doSend(); }
          }}
          placeholder={isClosed ? "Thread is closed" : "Type your message… (Shift+Enter for newline)"}
          disabled={isClosed || sending}
          className="min-h-0 resize-none flex-1 text-sm"
          data-testid={`${testIdPrefix}-input`}
        />
        <Button
          type="submit"
          disabled={isClosed || sending || !text.trim()}
          className="bg-brand hover:bg-brand-hover text-white h-10 w-10 p-0 shrink-0"
          data-testid={`${testIdPrefix}-send-btn`}
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </Button>
      </form>
    </div>
  );
};
